/**
 * Upgrade Success Page
 * Shown after a clinic successfully selects a paid plan.
 * Fully localized via provider.upgradeSuccessPage.* namespace.
 */
import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClinicData } from '../../context/ClinicDataContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Crown, ArrowRight, Star, Zap, Users,
  Shield, BarChart3, Rocket, Mail, Phone
} from 'lucide-react';
import confetti from 'canvas-confetti';

const UpgradeSuccessPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { clinicUser, createAndLoginNewClinic } = useClinicData();
  const confettiCanvasRef = React.useRef(null);

  const searchParams = new URLSearchParams(location.search);
  const packageName = location.state?.packageName || searchParams?.get('package') || t('provider.upgradeSuccessPage.yourPlanFallback');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      navigate(`/clinic/payment-success?session_id=${sessionId}&upgrade=true`, { replace: true });
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    if (!clinicUser) {
      createAndLoginNewClinic({
        email: 'new.clinic@treatx.demo',
        clinicName: 'My New Clinic',
      });
    }
  }, [clinicUser, createAndLoginNewClinic]);

  useEffect(() => {
    const canvas = document.createElement('canvas');
    canvas.style.position = 'fixed';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '9999';
    document.body.appendChild(canvas);
    confettiCanvasRef.current = canvas;

    const myConfetti = confetti.create(canvas, { resize: true });
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }
      myConfetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#9333ea', '#f59e0b', '#10b981'],
      });
      myConfetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#9333ea', '#f59e0b', '#10b981'],
      });
    }, 50);

    return () => {
      clearInterval(interval);
      myConfetti.reset();
      if (confettiCanvasRef.current && confettiCanvasRef.current.parentNode) {
        confettiCanvasRef.current.parentNode.removeChild(confettiCanvasRef.current);
      }
      confettiCanvasRef.current = null;
    };
  }, []);

  const premiumFeatures = [
    { icon: Zap, textKey: 'provider.upgradeSuccessPage.feat1Title', descKey: 'provider.upgradeSuccessPage.feat1Desc' },
    { icon: Users, textKey: 'provider.upgradeSuccessPage.feat2Title', descKey: 'provider.upgradeSuccessPage.feat2Desc' },
    { icon: Shield, textKey: 'provider.upgradeSuccessPage.feat3Title', descKey: 'provider.upgradeSuccessPage.feat3Desc' },
    { icon: BarChart3, textKey: 'provider.upgradeSuccessPage.feat4Title', descKey: 'provider.upgradeSuccessPage.feat4Desc' },
    { icon: Star, textKey: 'provider.upgradeSuccessPage.feat5Title', descKey: 'provider.upgradeSuccessPage.feat5Desc' },
  ];

  const nextSteps = [
    {
      titleKey: 'provider.upgradeSuccessPage.step1Title',
      descKey: 'provider.upgradeSuccessPage.step1Desc',
      actionKey: 'provider.upgradeSuccessPage.step1Action',
      path: '/clinic/admin',
    },
    {
      titleKey: 'provider.upgradeSuccessPage.step2Title',
      descKey: 'provider.upgradeSuccessPage.step2Desc',
      actionKey: 'provider.upgradeSuccessPage.step2Action',
      path: '/clinic/admin/users',
    },
    {
      titleKey: 'provider.upgradeSuccessPage.step3Title',
      descKey: 'provider.upgradeSuccessPage.step3Desc',
      actionKey: 'provider.upgradeSuccessPage.step3Action',
      path: '/clinic/admin',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50" data-testid="upgrade-success-page">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-amber-200/40 to-orange-200/40 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-yellow-200/40 to-amber-200/40 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="relative container mx-auto px-4 py-12 max-w-4xl">
        {/* Success Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 shadow-xl mb-6">
            <Crown className="h-12 w-12 text-white" />
          </div>
          <Badge className="bg-amber-100 text-amber-700 border-amber-300 mb-4 px-4 py-1">
            <Star className="h-3 w-3 mr-1" />
            {t('provider.upgradeSuccessPage.badgePremiumMember')}
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            {t('provider.upgradeSuccessPage.heroTitle', { plan: packageName })}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('provider.upgradeSuccessPage.heroSubtitle')}
          </p>
        </div>

        {/* Subscription Card */}
        <Card className="mb-8 border-2 border-amber-200 shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">{t('provider.upgradeSuccessPage.planSuffix', { plan: packageName })}</h2>
                <p className="text-amber-100">{t('provider.upgradeSuccessPage.subActive')}</p>
              </div>
              <div className="text-right">
                <Badge className="bg-white/20 text-white border-white/30">
                  {t('provider.upgradeSuccessPage.statusActive')}
                </Badge>
              </div>
            </div>
          </div>
          <CardContent className="p-6">
            <h3 className="font-semibold text-gray-900 mb-4">{t('provider.upgradeSuccessPage.yourPremiumFeatures')}</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {premiumFeatures.map((feature, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mx-auto mb-2">
                    <feature.icon className="h-6 w-6 text-amber-600" />
                  </div>
                  <p className="text-sm font-medium text-gray-900">{t(feature.textKey)}</p>
                  <p className="text-xs text-gray-500">{t(feature.descKey)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Card className="mb-8 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Rocket className="h-5 w-5 text-amber-600" />
              {t('provider.upgradeSuccessPage.getStarted')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {nextSteps.map((step, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-4 border rounded-xl hover:border-amber-300 hover:bg-amber-50/50 transition-all cursor-pointer group"
                  onClick={() => navigate(step.path)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center font-bold text-amber-600">
                      {idx + 1}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{t(step.titleKey)}</h3>
                      <p className="text-sm text-gray-500">{t(step.descKey)}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="text-amber-600 group-hover:bg-amber-100">
                    {t(step.actionKey)}
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Support Info */}
        <Card className="mb-8 bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{t('provider.upgradeSuccessPage.prioritySupportTitle')}</h3>
                <p className="text-sm text-gray-600 mb-3">
                  {t('provider.upgradeSuccessPage.prioritySupportDesc')}
                </p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2 text-purple-600">
                    <Mail className="h-4 w-4" />
                    <span>premium@treatx.health</span>
                  </div>
                  <div className="flex items-center gap-2 text-purple-600">
                    <Phone className="h-4 w-4" />
                    <span>{t('provider.upgradeSuccessPage.prioritySupportPhone')}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            size="lg"
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-lg"
            onClick={() => navigate('/clinic/admin')}
            data-testid="go-to-dashboard-btn"
          >
            {t('provider.upgradeSuccessPage.goToDashboard')}
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            onClick={() => navigate('/clinic/admin/users')}
          >
            {t('provider.upgradeSuccessPage.addTeamMembers')}
          </Button>
        </div>

        {/* Receipt Info */}
        <div className="text-center mt-10 text-sm text-gray-500">
          <p>{t('provider.upgradeSuccessPage.receiptFooter')}</p>
        </div>
      </div>
    </div>
  );
};

export default UpgradeSuccessPage;
